import { h, clear } from "@/lib/dom";
import { icon } from "@/lib/icons";
import {
  openModal,
  button,
  confirmDialog,
  toast,
  textInput,
} from "@/components/ui";
import { subjectsService, pbErrorMessage } from "@/services";
import { openSubjectForm } from "@/features/subjects/subjectForm";
import { openSubjectTemplates } from "@/features/subjects/subjectTemplates";
import { subjectIcon } from "@/lib/subjectIcons";
import { defaultColor, contrastText } from "@/lib/colors";
import type { Subject } from "@/types/models";

/**
 * Einstellungen: Fächer verwalten (wie zuvor) + ein kleiner Knopf für den
 * Bearbeitungsmodus. Neues Fach, Vorlagen, Reihenfolge & Kategorien per
 * Ziehen (Maus + Touch). Überschrift-Zeilen gruppieren: alle Fächer
 * unterhalb einer Überschrift (bis zur nächsten) gehören zu dieser
 * Kategorie. Die Zuordnung wird aus der sichtbaren Reihenfolge abgeleitet
 * und gespeichert (subjects.position + subjects.category).
 */
export function openSettings(opts: {
  subjects: Subject[];
  onChanged: () => void;
}): void {
  let list = [...opts.subjects];
  let changed = false;
  const listEl = h("div", { class: "manage-list" });

  // ---- Drag (Pointer Events: Maus + Touch) ----
  let dragRow: HTMLElement | null = null;

  function getAfterElement(y: number): HTMLElement | null {
    const els = Array.from(
      listEl.querySelectorAll<HTMLElement>(".manage-row:not(.dragging)")
    );
    let closestOffset = -Infinity;
    let closestEl: HTMLElement | null = null;
    for (const el of els) {
      const box = el.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;
      if (offset < 0 && offset > closestOffset) {
        closestOffset = offset;
        closestEl = el;
      }
    }
    return closestEl;
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!dragRow) return;
    const after = getAfterElement(e.clientY);
    if (after == null) listEl.appendChild(dragRow);
    else listEl.insertBefore(dragRow, after);
  };

  const onPointerUp = () => {
    document.removeEventListener("pointermove", onPointerMove);
    dragRow?.classList.remove("dragging");
    dragRow = null;
    void persistFromDom();
  };

  const attachDrag = (grip: HTMLElement, row: HTMLElement) => {
    grip.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      dragRow = row;
      row.classList.add("dragging");
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp, { once: true });
    });
  };

  /** Liest Reihenfolge + Kategorien aus dem DOM und speichert Änderungen. */
  async function persistFromDom() {
    const rows = Array.from(listEl.querySelectorAll<HTMLElement>(".manage-row"));
    let currentCat = "";
    const updates: { id: string; position: number; category: string }[] = [];
    let pos = 0;
    for (const row of rows) {
      if (row.dataset.heading != null) {
        currentCat = row.dataset.heading;
        continue;
      }
      const id = row.dataset.id!;
      updates.push({ id, position: pos++, category: currentCat });
    }
    changed = true;
    try {
      await Promise.all(
        updates.map((u) => {
          const s = list.find((x) => x.id === u.id);
          if (!s || (s.position === u.position && (s.category ?? "") === u.category))
            return null;
          return subjectsService.update(u.id, {
            position: u.position,
            category: u.category,
          });
        })
      );
      list = updates
        .map((u) => {
          const s = list.find((x) => x.id === u.id)!;
          return { ...s, position: u.position, category: u.category };
        })
        .filter(Boolean);
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /** Lädt die Fächerliste neu und rendert sie. */
  async function reload() {
    changed = true;
    try {
      list = await subjectsService.list();
      renderList();
    } catch {
      /* ignore – die Liste bleibt dann auf dem letzten Stand */
    }
  }

  // ---- Zeilen ----
  function headingRow(name: string): HTMLElement {
    const grip = h("span", { class: "manage-grip", html: icon("grip", 18) });
    const row = h(
      "div",
      { class: "manage-row manage-heading", dataset: { heading: name } },
      grip,
      h("span", { class: "manage-heading-name" }, name),
      button({
        icon: "x",
        variant: "ghost",
        size: "sm",
        title: "Überschrift entfernen (Fächer bleiben)",
        onClick: () => {
          row.remove();
          void persistFromDom();
        },
      })
    );
    attachDrag(grip, row);
    return row;
  }

  function subjectRow(s: Subject): HTMLElement {
    const accent = s.color || defaultColor();
    const grip = h("span", {
      class: "manage-grip",
      title: "Ziehen zum Verschieben",
      html: icon("grip", 18),
    });
    const row = h(
      "div",
      { class: "manage-row", dataset: { id: s.id } },
      grip,
      h("span", {
        class: "subject-icon sm",
        style: `background:${accent};color:${contrastText(accent)}`,
        html: subjectIcon(s.icon, 15),
      }),
      h("span", { class: "manage-name" }, s.title),
      button({
        icon: "pencil",
        variant: "ghost",
        size: "sm",
        title: "Bearbeiten",
        onClick: () => openSubjectForm({ subject: s, onSaved: () => void reload() }),
      }),
      button({
        icon: "trash",
        variant: "danger",
        size: "sm",
        title: "Löschen",
        onClick: () => void del(s),
      })
    );
    attachDrag(grip, row);
    return row;
  }

  function renderList() {
    clear(listEl);
    if (!list.length) {
      listEl.appendChild(
        h("p", { class: "field-hint" }, "Noch keine Fächer – lege eins an oder nutze eine Vorlage.")
      );
      return;
    }
    let lastCat: string | null = null;
    for (const s of list) {
      const cat = s.category ?? "";
      if (cat && cat !== lastCat) listEl.appendChild(headingRow(cat));
      lastCat = cat || lastCat;
      listEl.appendChild(subjectRow(s));
    }
  }

  async function del(s: Subject) {
    const ok = await confirmDialog({
      title: "Fach löschen?",
      message: `„${s.title}" samt allen Themen, Notizen und Materialien wird gelöscht.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await subjectsService.remove(s.id);
      list = list.filter((x) => x.id !== s.id);
      changed = true;
      renderList();
      toast("Fach gelöscht.", "success");
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  function addHeading() {
    const input = textInput({ placeholder: "z. B. MINT, Sprachen, Nebenfächer" });
    const err = h("div", { class: "field-error" });
    const sub = openModal({
      title: "Neue Überschrift",
      body: h("div", null, h("div", { class: "field" }, input), err),
      footer: h(
        "div",
        { class: "row" },
        button({ label: "Abbrechen", variant: "ghost", onClick: () => sub.close() }),
        button({
          label: "Hinzufügen",
          variant: "primary",
          onClick: () => {
            const name = input.value.trim();
            if (!name) {
              err.textContent = "Bitte einen Namen eingeben.";
              return;
            }
            listEl.prepend(headingRow(name));
            sub.close();
            void persistFromDom();
          },
        })
      ),
    });
    setTimeout(() => input.focus(), 50);
  }

  renderList();

  const handle = openModal({
    title: "Fächer verwalten",
    guardUnsaved: false, // alles speichert sofort
    body: h(
      "div",
      null,
      h(
        "div",
        { class: "row wrap", style: "margin-bottom:12px" },
        button({
          label: "Neues Fach",
          icon: "plus",
          variant: "primary",
          size: "sm",
          onClick: () => openSubjectForm({ onSaved: () => void reload() }),
        }),
        button({ label: "Neue Überschrift", icon: "plus", size: "sm", onClick: addHeading })
      ),
      h(
        "p",
        { class: "field-hint", style: "margin-bottom:10px" },
        "Am Griff ⠿ ziehen. Eine Überschrift gruppiert alle Fächer darunter (bis zur nächsten Überschrift)."
      ),
      listEl,
      h(
        "div",
        { class: "settings-links" },
        h(
          "button",
          {
            type: "button",
            class: "link-btn",
            onclick: () => openSubjectTemplates({ subjects: list, onChanged: () => void reload() }),
          },
          h("span", { class: "row", html: icon("layers", 14) }),
          "Aus Vorlage hinzufügen"
        )
      )
    ),
    footer: button({ label: "Fertig", variant: "primary", onClick: () => handle.close() }),
    onClose: () => {
      if (changed) opts.onChanged();
    },
  });
}
